import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { helperFunctions } from "../helper";
import AUTH_CONFIG from "../config/auth.config";
import { AppConstants } from "../constants";
import { toast } from "react-toastify";
import ENTRA_AUTH_CONFIG from "../config/entra.auth.config";

declare global {
  interface Window {
    _refreshTokenPromise?: Promise<any> | null;
  }
}

const http: AxiosInstance = axios.create({
  baseURL: "",
  headers: {
    "Content-Type": "application/json",
  },
});

// Interceptor for any REST Request
http.interceptors.request.use((request: any) => {
  if (!request.url.includes(`${AUTH_CONFIG.SSO_BASE_PATH}`) && !request.url.includes("login.microsoftonline.com")) {
    request = prepareCommonRequest(request);
  }
  return request;
});

const onSuccess = (response: AxiosResponse) => {
  if (response?.config?.url?.includes("export") || response?.config?.url?.includes("template") || response?.config?.url?.includes("job")) {
    response.data = { data: response.data, headers: response["headers"] };
  }
  if (response.config.method === "head") {
    response.data = { contentLength: response["headers"]["content-count"] };
  }
  return Promise.resolve(response.data);
};

const onError = (error: any) => {
  console.log("error is", error);
  const { config: originalReq, response } = error;
  console.log("originalReq is", originalReq, response);

  //throw error;
  // skip refresh token request, retry attempts to avoid infinite loops
  if (
    originalReq &&
    !originalReq.isRetryAttempt &&
    response &&
    response.status === 401
  ) {
    console.log("Unauthorized- Refreshing token");

    // return response && response.data ? Promise.reject(response.data) : Promise.reject(error);

    return new Promise((resolve, reject) => {
      // Ensure refreshAuthToken is called only once at a time
      const isKioskMode = helperFunctions.getKioskModeValue();
      if (!window._refreshTokenPromise) {
        if (isKioskMode) {
          window._refreshTokenPromise = refreshEntraToken().finally(() => {
            window._refreshTokenPromise = null;
          });
        } else {
          window._refreshTokenPromise = refreshAuthToken().finally(() => {
            window._refreshTokenPromise = null;
          });
        }
      }
      window._refreshTokenPromise.then(
        (newToken: any) => {
          originalReq.isRetryAttempt = true;
          //update authorization header with new token
          originalReq.headers[
            "Authorization"
          ] = `Bearer ${newToken?.access_token}`;
          axios.request(originalReq).then(
            (originalResponse) => {
              if (originalResponse?.config?.url?.includes("export") || originalResponse?.config?.url?.includes("template")) {
                originalResponse.data = { data: originalResponse.data, headers: originalResponse["headers"] };
              }
              if (originalResponse.config.method === "head") {
                originalResponse.data = { contentLength: originalResponse["headers"]["content-count"] };
              }
              resolve(originalResponse.data);
            },
            (error) => {
              console.log("Error while retrying request", error);
              toast.error("Error while retrying request after session expire", error);
              reject(error);
            }
          );
        },
        (error: any) => {
          console.error("Error while LOL refresh token", error);
          // TO DO  : this code will be called when refresh token api is called for single time and it fails
          toast.error("Session expired. Unable to refresh access token. Please log in again.");
          setTimeout(() => {
                helperFunctions.triggerCustomEvent(AppConstants.CUSTOM_EVENTS.LOG_OUT_USER);
          }, 200);
          reject(error);
        }
      );
    });
  } else {
    return response ? Promise.reject(response) : Promise.reject(error);
  }
};

const prepareCommonRequest = (request: any) => {
  if (request.options && request.options["withCredentials"]) {
    request["withCredentials"] = true;
  }
  if (request.options && request.options["iscontentSet"]) {
    delete request.headers["Content-Type"];
  } else if (request.options) {
    request.headers["Content-Type"] =
      request.options && request.options["Content-Type"];
  }

  // Add kiosk-specific headers
  const isKioskMode = helperFunctions.getKioskModeValue();
  if (isKioskMode) {
    request.headers["Mode"] = AppConstants.KIOSK;
  }

  // Add timezone to headers
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (timezone) {
    request.headers["Timezone"] = timezone;
  }

  // Add app-source header for OnFarm requests
  if(helperFunctions.isOnFarm()){
    request.headers["x-app-source"] = "onfarm";
  }
  const lolToken = helperFunctions.getLOLToken();
  const apiKey = helperFunctions.getAPIKey();

  if (lolToken && apiKey) {
    request.headers["X-Api-Key"] = apiKey;
    request.headers["Authorization"] = `Bearer ${lolToken}`;
  }
  return request;
};

  const refreshAuthToken = () => {
    let url = `${AUTH_CONFIG.SSO_BASE_PATH}${AUTH_CONFIG.TOKEN_URL}`;

    const existingTokenDetails: any = helperFunctions.getAuthTokenDetails();
    if (existingTokenDetails) {
      return new Promise((resolve, reject) => {
        // const refreshRequestBody =
        //   `grant_type=${AUTH_CONFIG.GRANT_TYPE_REFRESH}&client_id=${AUTH_CONFIG.SSO_CLIENT_ID}&refresh_token=` +
        //   existingTokenDetails.refresh_token +
        //   '&redirect_uri=' + ${AUTH_CONFIG.SSO_REDIRECT_URI};

        const refreshRequestBody = {
          client_id: `${AUTH_CONFIG.SSO_CLIENT_ID}`,
          // redirect_uri: `${AUTH_CONFIG.SSO_REDIRECT_URI}`,
          refresh_token: existingTokenDetails.refresh_token,
          grant_type: `${AUTH_CONFIG.GRANT_TYPE_REFRESH}`,
        };
        // To DO : Refresh API should be called only once and not for each request that gave 401
        NetworkService.post(url, refreshRequestBody, {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: "",
          },
        }).then(
          (accessTokenResponse: any) => {
            localStorage.setItem(
              AppConstants.SSO_AUTH_TOKEN_DETAIL,
              JSON.stringify(accessTokenResponse)
            );
            resolve(accessTokenResponse);
          },
          (error) => {

            // TO DO : This is not a correct solution. This needs to be fixed after the release
            // const tokenDetails = helperFunctions.getAuthTokenDetails();
            // if (tokenDetails) {
            //   resolve(tokenDetails);
            // } else {
            //   reject(error);
            // }
            reject(error);

          }
        );
      });
    } else {
      return Promise.reject("No token details found");
    }
  };

const refreshEntraToken = () => {
  let url =  `${ENTRA_AUTH_CONFIG.ENTRA_SSO_BASE_PATH}${ENTRA_AUTH_CONFIG.TOKEN_URL}`;

  const existingTokenDetails: any = helperFunctions.getAuthTokenDetails();
  if (existingTokenDetails) {
    return new Promise((resolve, reject) => {
      // const refreshRequestBody =
      //   `grant_type=${AUTH_CONFIG.GRANT_TYPE_REFRESH}&client_id=${AUTH_CONFIG.SSO_CLIENT_ID}&refresh_token=` +
      //   existingTokenDetails.refresh_token +
      //   '&redirect_uri=' + ${AUTH_CONFIG.SSO_REDIRECT_URI};

      const refreshRequestBody = {
        client_id: `${ENTRA_AUTH_CONFIG.ENTRA_SSO_CLIENT_ID}`,
        // redirect_uri: `${ENTRA_AUTH_CONFIG.SSO_REDIRECT_URI}`,
        refresh_token: existingTokenDetails.refresh_token,
        grant_type: `${ENTRA_AUTH_CONFIG.GRANT_TYPE_REFRESH}`,
      };
      // To DO : Refresh API should be called only once and not for each request that gave 401
      NetworkService.post(url, refreshRequestBody, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: "",
        },
      }).then(
        (accessTokenResponse: any) => {
          localStorage.setItem(
            AppConstants.SSO_AUTH_TOKEN_DETAIL,
            JSON.stringify(accessTokenResponse)
          );
          resolve(accessTokenResponse);
        },
        (error) => {

          // TO DO : This is not a correct solution. This needs to be fixed after the release
          // const tokenDetails = helperFunctions.getAuthTokenDetails();
          // if (tokenDetails) {
          //   resolve(tokenDetails);
          // } else {
          //   reject(error);
          // }
          reject(error);

        }
      );
    });
  } else {
    return Promise.reject("No token details found");
  }
};
http.interceptors.response.use(onSuccess, onError);

const NetworkService = {
  get: (url: string, responseType?: any, queryParams?: any) =>
    http({
      method: "GET",
      url: url,
      params: queryParams,
      responseType: responseType,
    }),
  patch: (url: string, requestBody?: any, options: AxiosRequestConfig = {}) =>
    http({
      method: "PATCH",
      url: url,
      data: requestBody,
      ...options,
    }),
  post: (url: string, requestBody: any, options: AxiosRequestConfig = {}) =>
    http({
      method: "POST",
      url: url,
      data: requestBody,
      ...options,
    }),
  search: (url: string, requestBody: any, options: any) =>
    http({
      method: "SEARCH",
      url: url,
      data: requestBody,
      ...options,
    }),
  put: (url: string, requestBody: any, options: AxiosRequestConfig = {}) =>
    http({
      method: "PUT",
      url: url,
      data: requestBody,
      ...options,
    }),

  delete: (url: string, responseType?: any, queryParams?: any) =>
    http({
      method: "DELETE",
      url: url,
      params: queryParams,
      responseType: responseType,
    }),
  head: (url: string, responseType?: any, queryParams?: any) =>
    http({
      method: "HEAD",
      url: url,
      params: queryParams,
      responseType: responseType,
    }),
};

export default NetworkService;
